import { useEffect, useState } from "react";
import axios from "axios";
import { X, ImageIcon, Camera } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/gallery`, { params: { limit: 60 } });
        if (!cancelled) setItems(Array.isArray(data) ? data : []);
      } catch {
        // silent — section just hides if API fails
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) return null;
  if (items.length === 0) return null;

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="bg-bone border-y-2 border-forest"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-20 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-6">
            <div className="text-xs uppercase tracking-[0.3em] text-safety font-bold">
              / Recent Jobs — Our Work
            </div>
            <h2 className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.9] mt-4">
              Big Trees.
              <br />
              <span className="text-safety">Clean Cuts.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 flex items-end">
            <p className="text-lg text-forest/80">
              A few jobs from around East Tennessee. Tap any photo to see it bigger. Your tree could
              be next.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
          {items.map((it, idx) => (
            <button
              key={it.id}
              data-testid={`gallery-thumb-${idx}`}
              onClick={() => setActive(it)}
              className="group relative aspect-square overflow-hidden border-2 border-forest hard-shadow text-left bg-forest"
              type="button"
              aria-label={`View ${it.title}`}
            >
              <img
                src={it.image_data}
                alt={it.title}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent p-3 sm:p-4">
                <div className="font-display tracking-tight text-white text-lg sm:text-xl uppercase line-clamp-2">
                  {it.title}
                </div>
              </div>
              <div className="absolute top-3 right-3 w-8 h-8 bg-safety border-2 border-bone flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <ImageIcon className="w-4 h-4 text-bone" strokeWidth={2.5} />
              </div>
            </button>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2 text-forest/70">
            <Camera className="w-4 h-4 text-safety" strokeWidth={2.5} />
            <span className="text-xs uppercase tracking-[0.25em] font-bold">
              {items.length} {items.length === 1 ? "Job" : "Jobs"} Posted
            </span>
          </div>
          <a
            href="#quote"
            data-testid="gallery-cta"
            className="btn-primary px-5 py-3 text-base flex items-center gap-2 ml-auto"
          >
            REQUEST YOUR FREE QUOTE
          </a>
        </div>
      </div>

      {/* Lightbox */}
      {active && (
        <div
          role="dialog"
          aria-modal="true"
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4 sm:p-10 cursor-zoom-out"
          onClick={() => setActive(null)}
        >
          <button
            data-testid="gallery-lightbox-close"
            onClick={() => setActive(null)}
            className="absolute top-4 right-4 w-12 h-12 bg-safety border-2 border-bone flex items-center justify-center text-bone hover:bg-bone hover:text-safety transition-colors"
            aria-label="Close"
            type="button"
          >
            <X className="w-6 h-6" strokeWidth={3} />
          </button>
          <div
            className="max-w-5xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={active.image_data}
              alt={active.title}
              className="w-full max-h-[75vh] object-contain border-2 border-bone"
            />
            <div className="bg-bone border-2 border-bone border-t-0 p-5">
              <div className="font-display text-3xl tracking-tight uppercase">{active.title}</div>
              {active.caption && (
                <p className="mt-2 text-forest/80">{active.caption}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
